import {
  AddProductToMyWishlistInput,
  CreateMyWishlistInput,
  DeleteProductFromMyWishlistInput,
  UpdateMyWishlistInput,
  UpdateProductQuantityFromMyWishlistInput
} from '@spa/generated/graphql/graphql';
import {
  AnalyticsProduct,
  WishlistTrackingProduct
} from '@spa/lib/tracking/productsToAnalyticsItemConverter';
import { Draft, castDraft } from 'immer';
import { HydrateAction, hydrateType } from '../HydrateAction';
import {
  ListAttribution,
  WishlistsActionsOriginType
} from '@spa/lib/tracking/trackWishlistsActions';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { StateWishlist, WishlistsState } from './WishlistsState';
import { SliceActions } from '@spa/lib/store/SliceActions';
import { WishlistError } from '@spa/components/wishlist/wishlistTypes';
import removeFirstMatchingItemFromArray from '@spa/lib/store/removeFirstMatchingItemFromArray';

interface SetMyWishlistsPayload {
  readonly localChange: boolean;
  readonly wishlists: ReadonlyArray<StateWishlist>;
}

export interface GetMyWishlistsRequestedPayload {
  readonly actionId: number;
}

export interface GetMyWishlistsSucceededPayload {
  readonly requestedActionId: number;
  readonly wishlists: ReadonlyArray<StateWishlist>;
}

export interface GetMyWishlistsFailedPayload {
  readonly error: WishlistError;
  readonly requestedActionId: number;
}

export interface DeleteMyWishlistInAddProductPayload {
  readonly id: string;
  readonly itemId: string;
}

export interface AddProductToMyWishlistRequestedPayload {
  readonly actionId: number;
  readonly deletedWishlist?: DeleteMyWishlistInAddProductPayload;
  readonly input: AddProductToMyWishlistInput;
  readonly isDefault: boolean;
  readonly listAttribution?: ListAttribution;
  readonly product: WishlistTrackingProduct;
}

export interface AddProductToMyWishlistSucceededPayload {
  readonly requestedActionId: number;
  readonly wishlists: ReadonlyArray<StateWishlist>;
}

export interface AddProductToMyWishlistFailedPayload {
  readonly error: WishlistError;
  readonly requestedActionId: number;
}

export interface CreateMyWishlistRequestedPayload {
  readonly actionId: number;
  readonly input: CreateMyWishlistInput;
  readonly isDefault?: boolean;
  readonly listAttribution?: ListAttribution;
  readonly origin?: WishlistsActionsOriginType;
  readonly products?: ReadonlyArray<WishlistTrackingProduct>;
  readonly deletedWishlist?: DeleteMyWishlistInAddProductPayload;
}

export interface CreateMyWishlistSucceededPayload {
  readonly requestedActionId: number;
  readonly wishlist: StateWishlist;
}

export interface CreateMyWishlistFailedPayload {
  readonly error: WishlistError;
  readonly requestedActionId: number;
}

export interface DeleteMyWishlistRequestedPayload {
  readonly actionId: number;
  readonly id: string;
}

export interface DeleteMyWishlistSucceededPayload {
  readonly id: string;
  readonly requestedActionId: number;
}

export interface DeleteMyWishlistFailedPayload {
  readonly error: WishlistError;
  readonly id: string;
  readonly requestedActionId: number;
}

export interface UpdateMyWishlistRequestedPayload {
  readonly actionId: number;
  readonly input: UpdateMyWishlistInput;
}

export interface UpdateMyWishlistSucceededPayload {
  readonly input: UpdateMyWishlistInput;
  readonly requestedActionId: number;
}

export interface UpdateMyWishlistFailedPayload {
  readonly errors: ReadonlyArray<WishlistError>;
  readonly requestedActionId: number;
}

export interface DeleteProductFromMyWishlistRequestedPayload {
  readonly actionId: number;
  readonly input: DeleteProductFromMyWishlistInput;
  readonly isDefault?: boolean;
  readonly product?: AnalyticsProduct;
}

export interface DeleteProductFromMyWishlistSucceededPayload {
  readonly input: DeleteProductFromMyWishlistInput;
  readonly requestedActionId: number;
}

export interface DeleteProductFromMyWishlistFailedPayload {
  readonly requestedActionId: number;
  readonly error: WishlistError;
}

export interface UpdateProductQuantityFromMyWishlistRequestedPayload {
  readonly actionId: number;
  readonly input: UpdateProductQuantityFromMyWishlistInput;
}

export interface UpdateProductQuantityFromMyWishlistSucceededPayload {
  readonly requestedActionId: number;
  readonly input: UpdateProductQuantityFromMyWishlistInput;
}

export interface UpdateProductQuantityFromMyWishlistFailedPayload {
  readonly requestedActionId: number;
  readonly error: WishlistError;
}

const initialState: WishlistsState = {
  pageActions: {
    addProductToMyWishlist: { failed: [], pending: [], succeeded: [] },
    createMyWishlist: { failed: [], pending: [], succeeded: [] },
    deleteMyWishlist: { failed: [], pending: [], succeeded: [] },
    deleteProductFromMyWishlist: { failed: [], pending: [], succeeded: [] },
    getMyWishlists: { failed: [], pending: [], succeeded: [] },
    updateMyWishlist: { failed: [], pending: [], succeeded: [] },
    updateProductQuantityFromMyWishlist: { failed: [], pending: [], succeeded: [] }
  },
  wishlists: [],
  version: 0
};

export const wishlistsSlice = createSlice({
  name: 'wishlists',
  initialState,
  reducers: {
    addProductToMyWishlistRequested: (
      {
        pageActions: {
          addProductToMyWishlist: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<AddProductToMyWishlistRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    addProductToMyWishlistSucceeded: (
      state: Draft<WishlistsState>,
      {
        payload: { requestedActionId, wishlists }
      }: PayloadAction<AddProductToMyWishlistSucceededPayload>
    ): void => {
      const {
        pageActions: {
          addProductToMyWishlist: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ requestedActionId });
      state.wishlists = castDraft(wishlists);
      state.version = castDraft(state.version + 1);
    },
    addProductToMyWishlistFailed: (
      {
        pageActions: {
          addProductToMyWishlist: { failed, pending }
        }
      }: Draft<WishlistsState>,
      { payload: { requestedActionId } }: PayloadAction<AddProductToMyWishlistFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({ requestedActionId });
    },
    setWishlists: (
      state: Draft<WishlistsState>,
      { payload: { wishlists } }: PayloadAction<SetMyWishlistsPayload>
    ): void => {
      state.wishlists = castDraft(wishlists);
      state.version = castDraft(state.version + 1);
    },
    getMyWishlistsRequested: (
      {
        pageActions: {
          getMyWishlists: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<GetMyWishlistsRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    getMyWishlistsSucceeded: (
      state: Draft<WishlistsState>,
      { payload: { requestedActionId, wishlists } }: PayloadAction<GetMyWishlistsSucceededPayload>
    ): void => {
      const {
        pageActions: {
          getMyWishlists: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ requestedActionId });
      state.wishlists = castDraft(wishlists);
      state.version = castDraft(state.version + 1);
    },
    getMyWishlistsFailed: (
      {
        pageActions: {
          getMyWishlists: { failed, pending }
        }
      }: Draft<WishlistsState>,
      { payload: { requestedActionId } }: PayloadAction<GetMyWishlistsFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({ requestedActionId });
    },
    createMyWishlistRequested: (
      {
        pageActions: {
          createMyWishlist: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<CreateMyWishlistRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    createMyWishlistSucceeded: (
      state: Draft<WishlistsState>,
      { payload: { requestedActionId, wishlist } }: PayloadAction<CreateMyWishlistSucceededPayload>
    ): void => {
      const {
        pageActions: {
          createMyWishlist: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ requestedActionId });
      state.wishlists.push(castDraft(wishlist));
      state.version = castDraft(state.version + 1);
    },
    createMyWishlistFailed: (
      {
        pageActions: {
          createMyWishlist: { failed, pending }
        }
      }: Draft<WishlistsState>,
      { payload: { requestedActionId } }: PayloadAction<CreateMyWishlistFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({ requestedActionId });
    },
    deleteMyWishlistRequested: (
      {
        pageActions: {
          deleteMyWishlist: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<DeleteMyWishlistRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    deleteMyWishlistSucceeded: (
      state: Draft<WishlistsState>,
      { payload: { id, requestedActionId } }: PayloadAction<DeleteMyWishlistSucceededPayload>
    ): void => {
      const {
        pageActions: {
          deleteMyWishlist: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ id, requestedActionId });
      state.wishlists = state.wishlists.filter((wishlist: StateWishlist) => id !== wishlist.id);
      state.version = castDraft(state.version + 1);
    },
    deleteMyWishlistFailed: (
      {
        pageActions: {
          deleteMyWishlist: { failed, pending }
        }
      }: Draft<WishlistsState>,
      { payload: { id, requestedActionId } }: PayloadAction<DeleteMyWishlistFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({ id, requestedActionId });
    },
    updateMyWishlistRequested: (
      {
        pageActions: {
          updateMyWishlist: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<UpdateMyWishlistRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    updateMyWishlistSucceeded: (
      state: Draft<WishlistsState>,
      {
        payload: {
          input: { description, id, name },
          requestedActionId
        }
      }: PayloadAction<UpdateMyWishlistSucceededPayload>
    ): void => {
      const {
        pageActions: {
          updateMyWishlist: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ requestedActionId });
      state.wishlists = castDraft(
        state.wishlists.map(
          (wishlist: StateWishlist): StateWishlist =>
            wishlist.id === id
              ? {
                  ...wishlist,
                  description: description ?? wishlist.description,
                  name: name ?? wishlist.name
                }
              : wishlist
        )
      );
      state.version = castDraft(state.version + 1);
    },
    updateMyWishlistFailed: (
      {
        pageActions: {
          updateMyWishlist: { failed, pending }
        }
      }: Draft<WishlistsState>,
      { payload: { errors, requestedActionId } }: PayloadAction<UpdateMyWishlistFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({
        errors: castDraft(errors),
        requestedActionId
      });
    },
    deleteProductFromMyWishlistRequested: (
      {
        pageActions: {
          deleteProductFromMyWishlist: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<DeleteProductFromMyWishlistRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    deleteProductFromMyWishlistSucceeded: (
      state: Draft<WishlistsState>,
      {
        payload: {
          requestedActionId,
          input: { id, itemId }
        }
      }: PayloadAction<DeleteProductFromMyWishlistSucceededPayload>
    ): void => {
      const {
        pageActions: {
          deleteProductFromMyWishlist: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ requestedActionId });
      state.wishlists = castDraft(
        state.wishlists.map((wishlist) =>
          wishlist.id === id
            ? {
                ...wishlist,
                items: wishlist.items.filter((item) => item.id !== itemId)
              }
            : wishlist
        )
      );
      state.version = castDraft(state.version + 1);
    },
    deleteProductFromMyWishlistFailed: (
      {
        pageActions: {
          updateProductQuantityFromMyWishlist: { pending, failed }
        }
      }: Draft<WishlistsState>,
      {
        payload: { requestedActionId }
      }: PayloadAction<UpdateProductQuantityFromMyWishlistFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({ requestedActionId });
    },
    updateProductQuantityFromMyWishlistRequested: (
      {
        pageActions: {
          updateProductQuantityFromMyWishlist: { pending }
        }
      }: Draft<WishlistsState>,
      { payload: { actionId } }: PayloadAction<UpdateProductQuantityFromMyWishlistRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    updateProductQuantityFromMyWishlistSucceeded: (
      state: Draft<WishlistsState>,
      {
        payload: {
          requestedActionId,
          input: { id, itemId, quantity }
        }
      }: PayloadAction<UpdateProductQuantityFromMyWishlistSucceededPayload>
    ): void => {
      const {
        pageActions: {
          updateProductQuantityFromMyWishlist: { pending, succeeded }
        }
      }: Draft<WishlistsState> = state;
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      succeeded.push({ requestedActionId });
      state.wishlists = castDraft(
        state.wishlists.map((wishlist) =>
          wishlist.id === id
            ? {
                ...wishlist,
                items: wishlist.items.map((item) =>
                  item.id === itemId ? { ...item, quantity } : item
                )
              }
            : wishlist
        )
      );
      state.version = castDraft(state.version + 1);
    },
    updateProductQuantityFromMyWishlistFailed: (
      {
        pageActions: {
          deleteProductFromMyWishlist: { pending, failed }
        }
      }: Draft<WishlistsState>,
      { payload: { requestedActionId } }: PayloadAction<DeleteProductFromMyWishlistFailedPayload>
    ): void => {
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      failed.push({ requestedActionId });
    }
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateType, (state: Draft<WishlistsState>, { payload }: HydrateAction) => {
      const wishlists = payload.wishlists;
      if (wishlists && state.version < wishlists.version) {
        state.wishlists = castDraft(wishlists.wishlists);
      }
    });
  }
});

export const {
  setWishlists,
  addProductToMyWishlistRequested,
  addProductToMyWishlistSucceeded,
  addProductToMyWishlistFailed,
  createMyWishlistRequested,
  createMyWishlistSucceeded,
  createMyWishlistFailed,
  deleteMyWishlistRequested,
  deleteMyWishlistSucceeded,
  deleteMyWishlistFailed,
  deleteProductFromMyWishlistRequested,
  deleteProductFromMyWishlistSucceeded,
  deleteProductFromMyWishlistFailed,
  getMyWishlistsRequested,
  getMyWishlistsSucceeded,
  getMyWishlistsFailed,
  updateMyWishlistRequested,
  updateMyWishlistSucceeded,
  updateMyWishlistFailed,
  updateProductQuantityFromMyWishlistRequested,
  updateProductQuantityFromMyWishlistSucceeded,
  updateProductQuantityFromMyWishlistFailed
} = wishlistsSlice.actions;

export type ActionTypes = SliceActions<typeof wishlistsSlice.actions>;

export default wishlistsSlice.reducer;

export const addProductToMyWishlistRequestedType = 'wishlists/addProductToMyWishlistRequested';
export type AddProductToMyWishlistRequestedAction = Extract<
  ActionTypes,
  PayloadAction<AddProductToMyWishlistRequestedPayload, typeof addProductToMyWishlistRequestedType>
>;

export const addProductToMyWishlistSucceededType = 'wishlists/addProductToMyWishlistSucceeded';
export type AddProductToMyWishlistSucceededAction = Extract<
  ActionTypes,
  PayloadAction<AddProductToMyWishlistSucceededPayload, typeof addProductToMyWishlistSucceededType>
>;

export const addProductToMyWishlistFailedType = 'wishlists/addProductToMyWishlistFailed';
export type AddProductToMyWishlistFailedAction = Extract<
  ActionTypes,
  PayloadAction<AddProductToMyWishlistFailedPayload, typeof addProductToMyWishlistFailedType>
>;

export const getMyWishlistsRequestedType = 'wishlists/getMyWishlistsRequested';
export type GetMyWishlistsRequestedAction = Extract<
  ActionTypes,
  PayloadAction<GetMyWishlistsRequestedPayload, typeof getMyWishlistsRequestedType>
>;

export const getMyWishlistsSucceededType = 'wishlists/getMyWishlistsSucceeded';
export type GetMyWishlistsSucceededAction = Extract<
  ActionTypes,
  PayloadAction<GetMyWishlistsSucceededPayload, typeof getMyWishlistsSucceededType>
>;

export const getMyWishlistsFailedType = 'wishlists/getMyWishlistsFailed';
export type GetMyWishlistsFailedAction = Extract<
  ActionTypes,
  PayloadAction<GetMyWishlistsFailedPayload, typeof getMyWishlistsFailedType>
>;

export const createMyWishlistRequestedType = 'wishlists/createMyWishlistRequested';
export type CreateMyWishlistRequestedAction = Extract<
  ActionTypes,
  PayloadAction<CreateMyWishlistRequestedPayload, typeof createMyWishlistRequestedType>
>;

export const createMyWishlistSucceededType = 'wishlists/createMyWishlistSucceeded';
export type CreateMyWishlistSucceededAction = Extract<
  ActionTypes,
  PayloadAction<CreateMyWishlistSucceededPayload, typeof createMyWishlistSucceededType>
>;

export const createMyWishlistFailedType = 'wishlists/createMyWishlistFailed';
export type CreateMyWishlistFailedAction = Extract<
  ActionTypes,
  PayloadAction<CreateMyWishlistFailedPayload, typeof createMyWishlistFailedType>
>;

export const deleteMyWishlistRequestedType = 'wishlists/deleteMyWishlistRequested';
export type DeleteMyWishlistRequestedAction = Extract<
  ActionTypes,
  PayloadAction<DeleteMyWishlistRequestedPayload, typeof deleteMyWishlistRequestedType>
>;

export const deleteMyWishlistSucceededType = 'wishlists/deleteMyWishlistSucceeded';
export type DeleteMyWishlistSucceededAction = Extract<
  ActionTypes,
  PayloadAction<DeleteMyWishlistSucceededPayload, typeof deleteMyWishlistSucceededType>
>;

export const deleteMyWishlistFailedType = 'wishlists/deleteMyWishlistFailed';
export type DeleteMyWishlistFailedAction = Extract<
  ActionTypes,
  PayloadAction<DeleteMyWishlistFailedPayload, typeof deleteMyWishlistFailedType>
>;

export const deleteProductFromMyWishlistRequestedType =
  'wishlists/deleteProductFromMyWishlistRequested';
export type DeleteProductFromMyWishlistRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    DeleteProductFromMyWishlistRequestedPayload,
    typeof deleteProductFromMyWishlistRequestedType
  >
>;

export const deleteProductFromMyWishlistSucceededType =
  'wishlists/deleteProductFromMyWishlistSucceeded';
export type DeleteProductFromMyWishlistSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    DeleteProductFromMyWishlistSucceededPayload,
    typeof deleteProductFromMyWishlistSucceededType
  >
>;

export const deleteProductFromMyWishlistFailedType = 'wishlists/deleteProductFromMyWishlistFailed';
export type DeleteProductFromMyWishlistFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    DeleteProductFromMyWishlistFailedPayload,
    typeof deleteProductFromMyWishlistFailedType
  >
>;

export const updateMyWishlistRequestedType = 'wishlists/updateMyWishlistRequested';
export type UpdateMyWishlistRequestedAction = Extract<
  ActionTypes,
  PayloadAction<UpdateMyWishlistRequestedPayload, typeof updateMyWishlistRequestedType>
>;

export const updateMyWishlistSucceededType = 'wishlists/updateMyWishlistSucceeded';
export type UpdateMyWishlistSucceededAction = Extract<
  ActionTypes,
  PayloadAction<UpdateMyWishlistSucceededPayload, typeof updateMyWishlistSucceededType>
>;

export const updateMyWishlistFailedType = 'wishlists/updateMyWishlistFailed';
export type UpdateMyWishlistFailedAction = Extract<
  ActionTypes,
  PayloadAction<UpdateMyWishlistFailedPayload, typeof updateMyWishlistFailedType>
>;

export const updateProductQuantityFromMyWishlistRequestedType =
  'wishlists/updateProductQuantityFromMyWishlistRequested';
export type UpdateProductQuantityFromMyWishlistRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    UpdateProductQuantityFromMyWishlistRequestedPayload,
    typeof updateProductQuantityFromMyWishlistRequestedType
  >
>;

export const updateProductQuantityFromMyWishlistSucceededType =
  'wishlists/updateProductQuantityFromMyWishlistSucceeded';
export type UpdateProductQuantityFromMyWishlistSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    UpdateProductQuantityFromMyWishlistSucceededPayload,
    typeof updateProductQuantityFromMyWishlistSucceededType
  >
>;

export const updateProductQuantityFromMyWishlistFailedType =
  'wishlists/updateProductQuantityFromMyWishlistFailed';
export type UpdateProductQuantityFromMyWishlistFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    UpdateProductQuantityFromMyWishlistFailedPayload,
    typeof updateProductQuantityFromMyWishlistFailedType
  >
>;
